import { orderAllActiveLineItemsCompleted } from './orderKitchenCompletion'

export const STATION_MINIMIZE_MS = 10_000

export interface OrderItemStationLike {
  foodId: string
  station?: string
  completed?: boolean
  cancelledAt?: string
}

export interface FoodStationLike {
  id: string
  name?: string
  station?: string
}

export interface StationProcessingEntry {
  minimizedAt?: string
  stationDoneAt?: string
}

export interface OrderStationProcessingLike {
  id: string
  status: string
  items: OrderItemStationLike[]
  stationProcessing?: Record<string, StationProcessingEntry>
}

export function getLineStation(
  item: OrderItemStationLike,
  food?: FoodStationLike | null,
): string | undefined {
  const s = item.station ?? food?.station
  const trimmed = typeof s === 'string' ? s.trim() : ''
  return trimmed.length > 0 ? trimmed : undefined
}

export function orderActiveItems(order: OrderStationProcessingLike): OrderItemStationLike[] {
  return order.items.filter((i) => !i.cancelledAt)
}

export function orderItemProgress(order: OrderStationProcessingLike): { done: number; total: number } {
  const active = orderActiveItems(order)
  const done = active.filter((i) => i.completed === true).length
  return { done, total: active.length }
}

export function itemMatchesStation(
  item: OrderItemStationLike,
  food: FoodStationLike | undefined,
  station: string,
): boolean {
  return getLineStation(item, food) === station
}

export function orderHasItemsForStation(
  order: OrderStationProcessingLike,
  menu: readonly FoodStationLike[],
  station: string,
): boolean {
  const menuById = new Map(menu.map((f) => [f.id, f]))
  return order.items.some((item) => {
    if (item.cancelledAt) return false
    const food = menuById.get(item.foodId)
    return itemMatchesStation(item, food, station)
  })
}

export interface StationLineRow {
  item: OrderItemStationLike
  index: number
  food?: FoodStationLike
}

export function stationLineItems(
  order: OrderStationProcessingLike,
  menu: readonly FoodStationLike[],
  station: string,
): StationLineRow[] {
  const menuById = new Map(menu.map((f) => [f.id, f]))
  const rows: StationLineRow[] = []
  order.items.forEach((item, index) => {
    if (item.cancelledAt) return
    const food = menuById.get(item.foodId)
    if (!itemMatchesStation(item, food, station)) return
    rows.push({ item, index, food })
  })
  return rows
}

export function allStationLineItemsCompleted(
  order: OrderStationProcessingLike,
  menu: readonly FoodStationLike[],
  station: string,
): boolean {
  const rows = stationLineItems(order, menu, station)
  if (rows.length === 0) return false
  return rows.every((r) => r.item.completed === true)
}

/** True when the last station line was checked and the 10s minimize countdown should start. */
export function shouldAutoStartStationMinimize(
  order: OrderStationProcessingLike,
  menu: readonly FoodStationLike[],
  station: string,
): boolean {
  if (!station.trim()) return false
  if (shouldFinishOrder(order)) return false
  if (isStationDone(order, station)) return false
  if (isStationMinimizing(order, station)) return false
  return allStationLineItemsCompleted(order, menu, station)
}

export function distinctStationsOnOrder(
  order: OrderStationProcessingLike,
  menu: readonly FoodStationLike[],
): string[] {
  const menuById = new Map(menu.map((f) => [f.id, f]))
  const set = new Set<string>()
  for (const item of order.items) {
    if (item.cancelledAt) continue
    const food = menuById.get(item.foodId)
    const st = getLineStation(item, food)
    if (st) set.add(st)
  }
  return [...set].sort()
}

export function getStationProcessingEntry(
  order: OrderStationProcessingLike,
  station: string,
): StationProcessingEntry | undefined {
  return order.stationProcessing?.[station]
}

export function isStationMinimizing(order: OrderStationProcessingLike, station: string): boolean {
  const entry = getStationProcessingEntry(order, station)
  if (!entry?.minimizedAt || entry.stationDoneAt) return false
  return true
}

export function isStationDone(order: OrderStationProcessingLike, station: string): boolean {
  return Boolean(getStationProcessingEntry(order, station)?.stationDoneAt)
}

export function isStationAwaitingOnBoard(
  order: OrderStationProcessingLike,
  station: string,
): boolean {
  if (!isStationDone(order, station)) return false
  return !shouldFinishOrder(order)
}

export function clearStationMinimizing<T extends OrderStationProcessingLike>(
  order: T,
  station: string,
): T {
  const entry = getStationProcessingEntry(order, station)
  if (!entry) return order
  const nextProcessing = { ...order.stationProcessing }
  delete nextProcessing[station]
  return { ...order, stationProcessing: Object.keys(nextProcessing).length > 0 ? nextProcessing : undefined }
}

export function shouldFinishOrder(order: OrderStationProcessingLike): boolean {
  return orderAllActiveLineItemsCompleted(order)
}

export function stationMinimizeRemainingMs(
  order: OrderStationProcessingLike,
  station: string,
  nowMs = Date.now(),
): number | null {
  const entry = getStationProcessingEntry(order, station)
  if (!entry?.minimizedAt || entry.stationDoneAt) return null
  const start = Date.parse(entry.minimizedAt)
  if (!Number.isFinite(start)) return null
  return Math.max(0, STATION_MINIMIZE_MS - (nowMs - start))
}

export function partitionProcessingForStation<
  T extends OrderStationProcessingLike,
>(orders: readonly T[], menu: readonly FoodStationLike[], station: string): {
  active: T[]
  awaiting: T[]
} {
  const active: T[] = []
  const awaiting: T[] = []
  for (const order of orders) {
    if (!orderHasItemsForStation(order, menu, station)) continue
    if (shouldFinishOrder(order)) continue
    if (isStationAwaitingOnBoard(order, station)) {
      awaiting.push(order)
    } else if (!isStationDone(order, station)) {
      active.push(order)
    }
  }
  return { active, awaiting }
}

export function awaitingProgressLabel(order: OrderStationProcessingLike): string {
  const { done, total } = orderItemProgress(order)
  const ratio = `${done}/${total}`
  return `${ratio} items done · waiting on others`
}
